import { FC } from 'react';
import { createFragmentContainer, graphql } from 'react-relay/legacy';
import { filterNulls } from 'dibs-ts-utils/exports/filterNulls';
import { GridRow } from 'dibs-elements/exports/Grid';
import { InViewport } from 'dibs-in-viewport/exports/InViewport';

import HpSharedPromoModulePromoItem from './HpSharedPromoModulePromoItem';
import { HpSharedModuleContainer } from '../HpSharedModuleContainer/HpSharedModuleContainer';
import { trackPromoImpressions, trackPromoClick } from './helpers/PromoTracking';
import { trackModuleLocation, trackModule } from '../helpers/moduleLocationTracking';
import { HpSharedHeader } from '../HpSharedHeader/HpSharedHeader';

import { HpSharedPromoModuleSecondary_componentModule$data } from './__generated__/HpSharedPromoModuleSecondary_componentModule.graphql';

type Props = {
    componentModule: HpSharedPromoModuleSecondary_componentModule$data;
    promoPrimaryItemsCount: number;
    useLazyLoadImages: boolean;
    isMobile: boolean;
    moduleIndex: number;
    totalModules: number;
};

const HpSharedPromoModuleSecondary: FC<Props> = ({
    componentModule,
    promoPrimaryItemsCount,
    useLazyLoadImages,
    isMobile,
    moduleIndex,
    totalModules,
}) => {
    if (!componentModule) {
        return null;
    }

    const { messages, cmsDisplayTitle } = componentModule;
    const { title } = messages || {};
    const items = (messages?.items || []).filter(filterNulls);
    const isSinglePromo = items.length === 1;

    const handleInitialDisplay = ({ inViewport }: { inViewport: boolean }): void => {
        if (componentModule && inViewport) {
            trackPromoImpressions({
                items,
                startingIndex: promoPrimaryItemsCount,
            });
        }
    };

    return (
        <HpSharedModuleContainer addTopGap={!title}>
            <HpSharedHeader title={title} />
            <InViewport stopWhenInViewport onInViewportChange={handleInitialDisplay}>
                <GridRow>
                    {items.map((promotion, index) => (
                        <HpSharedPromoModulePromoItem
                            key={promotion?.cmsId || index}
                            promotion={promotion}
                            useLazyLoadImages={useLazyLoadImages}
                            onClick={() => {
                                const promoIndex = index + promoPrimaryItemsCount;
                                trackPromoClick({
                                    item: promotion,
                                    index: promoIndex,
                                });
                                trackModuleLocation({
                                    label: `promo banner #${promoIndex}`,
                                    moduleIndex,
                                    totalModules,
                                });
                                trackModule(cmsDisplayTitle);
                            }}
                            isSinglePromo={isSinglePromo}
                            isMobile={isMobile}
                        />
                    ))}
                </GridRow>
            </InViewport>
        </HpSharedModuleContainer>
    );
};

export default createFragmentContainer(HpSharedPromoModuleSecondary, {
    componentModule: graphql`
        fragment HpSharedPromoModuleSecondary_componentModule on PromoModule {
            cmsDisplayTitle
            messages(userType: $userType) {
                title
                items {
                    cmsId
                    linkData {
                        # path is used in PromoTrackingOld.js
                        path
                    }
                    ...HpSharedPromoModulePromoItem_promotion
                }
            }
        }
    `,
});
